import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const SUGGESTIONS = [
  "Qual è il vino più fruttato?",
  "Cosa mi consigli per una cena a base di tartufo?",
  "Un rosso per la pizza?",
];

function getSessionId() {
  let sid = localStorage.getItem("uvashop_chat_sid");
  if (!sid) {
    sid = `chat-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem("uvashop_chat_sid", sid);
  }
  return sid;
}

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);
  const { open: cartOpen } = useCart();

  useEffect(() => {
    if (open && messages.length === 0) {
      api.get(`/chat/${sessionId.current}`).then(({ data }) => {
        if (data.messages?.length) setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      }).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Mi scuso, al momento non riesco a rispondere. Riprova tra poco." }]);
    } finally { setSending(false); }
  };

  const surprise = async () => {
    if (sending) return;
    setMessages((m) => [...m, { role: "user", content: "✨ Stupiscimi" }]);
    setSending(true);
    try {
      const { data } = await api.post("/sommelier/surprise", { session_id: sessionId.current });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Mi scuso, al momento non riesco a proporti nulla. Riprova tra poco." }]);
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Sommelier virtuale"
        className={`fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-claret text-bone shadow-xl flex items-center justify-center hover:scale-105 hover:bg-claret-dark transition-all ${cartOpen ? "hidden" : ""}`}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-widget-panel"
            className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-[400px] h-[560px] max-h-[75vh] bg-bone border border-ink/15 shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-ink text-bone px-5 py-4 flex items-center gap-3 flex-shrink-0">
              <span className="w-9 h-9 rounded-full bg-claret flex items-center justify-center"><Sparkles size={16} /></span>
              <div>
                <p className="font-serif-display text-lg leading-none">Sommelier Virtuale</p>
                <p className="text-[11px] text-bone/50 mt-1">Chiedimi un consiglio sul vino</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && !sending && (
                <div className="text-center py-6">
                  <p className="text-ink-soft text-sm mb-4">Ciao! Sono il tuo sommelier. Posso consigliarti il vino giusto dal nostro catalogo.</p>
                  <button
                    data-testid="chat-surprise-empty"
                    onClick={surprise}
                    className="flex items-center justify-center gap-2 w-full bg-claret text-bone hover:bg-claret-dark px-3 py-2.5 text-sm uppercase tracking-[0.12em] transition-colors mb-3"
                  >
                    <Sparkles size={15} /> Stupiscimi
                  </button>
                  <p className="text-ink-soft/70 text-[11px] mb-4">Non sai cosa scegliere? Ti propongo io due bottiglie.</p>
                  <div className="space-y-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} className="block w-full text-left text-sm border border-ink/15 hover:border-claret hover:text-claret px-3 py-2 transition-colors">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-claret text-bone" : "bg-white border border-ink/10 text-ink"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white border border-ink/10 px-3.5 py-2.5 text-sm text-ink-soft flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Sto pensando…</div>
                </div>
              )}
            </div>

            <div className="px-3 pt-2 flex-shrink-0">
              <button
                data-testid="chat-surprise"
                onClick={surprise}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 border border-claret text-claret hover:bg-claret hover:text-bone px-3 py-2 text-xs uppercase tracking-[0.12em] transition-colors disabled:opacity-50"
              >
                <Sparkles size={14} /> Stupiscimi
              </button>
            </div>
            <div className="p-3 border-t border-ink/10 flex-shrink-0 flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Scrivi un messaggio…"
                className="flex-1 bg-transparent border border-ink/20 focus:border-claret outline-none px-3 py-2.5 text-sm transition-colors"
              />
              <button data-testid="chat-send" onClick={() => send()} disabled={sending || !input.trim()} className="w-11 bg-claret hover:bg-claret-dark text-bone flex items-center justify-center transition-colors disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
